<script lang="ts" setup>
  import { computed, toRef } from 'vue';
  import { useField } from 'vee-validate';
  import TextInput from './TextInput.vue';
  import {
    type InputTransform,
    resolveInputTransform,
  } from '../inputTransforms/';
  import type { TextInputProps } from './TextInput.types';

  type TextInputFieldProps = Omit<TextInputProps, 'modelValue'> & {
    name: string;
    rules?: (value: string | null) => string | boolean;
    transform?: InputTransform;
  };

  const props = defineProps<TextInputFieldProps>();

  const {
    value: fieldValue,
    errorMessage,
    handleChange,
    handleBlur,
    meta,
  } = useField<string | null>(() => props.name, props.rules, {
    label: toRef(props, 'label'),
  });

  const displayError = computed(() =>
    meta.touched ? errorMessage.value : undefined,
  );

  const onChange = (value: string) => {
    const { transform } = props;
    handleChange(transform ? resolveInputTransform(transform)(value) : value);
  };
</script>

<template>
  <TextInput
    :id="id"
    :test-id="testId"
    :label="label"
    :helper-text="helperText"
    :warning="warning"
    :name="name"
    :type="type"
    :input-mode="inputMode"
    :pattern="pattern"
    :autocomplete="autocomplete"
    :placeholder="placeholder"
    :disabled="disabled"
    :disabled-reason="disabledReason"
    :required="required"
    :autofocus="autofocus"
    :size="size"
    :model-value="fieldValue ?? null"
    :error="displayError"
    @update:model-value="onChange"
    @blur="() => handleBlur()"
  >
    <template v-if="$slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>
    <template v-if="$slots.leadingIcon" #leadingIcon>
      <slot name="leadingIcon" />
    </template>
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
  </TextInput>
</template>
